import { Problem } from '../../types/problem';

export const stringProblems: Problem[] = [
  {
    id: 'string-1',
    title: 'Recursive String Reversal',
    description: `Write a recursive function to reverse a string.

**Example 1:**
Input: str = "hello"
Output: "olleh"

**Example 2:**
Input: str = "recursion"
Output: "noisrucer"

**Constraints:**
- Do not use built-in reverse methods
- Use recursion to solve this problem`,
    difficulty: 'Easy',
    tags: ['string', 'recursion'],
    hints: [
      'Think about how to break down the string into smaller parts',
      'Consider what happens when you take the first character and put it at the end',
      'Base case: what is the reverse of an empty string or single character?'
    ],
    starterCode: `function reverseString(str: string): string {
  // Your code here
}`,
    solution: `function reverseString(str: string): string {
  if (str.length <= 1) {
    return str;
  }
  return reverseString(str.slice(1)) + str[0];
}`,
    testCases: [
      {
        input: ["hello"],
        output: "olleh"
      },
      {
        input: ["recursion"],
        output: "noisrucer"
      },
      {
        input: ["a"],
        output: "a"
      },
      {
        input: [""],
        output: ""
      }
    ]
  },
  {
    id: 'string-2',
    title: 'Palindrome Check',
    description: `Write a recursive function to check if a string is a palindrome.
Ignore spaces, punctuation, and letter casing.

**Example 1:**
Input: str = "A man, a plan, a canal: Panama"
Output: true

**Example 2:**
Input: str = "race a car"
Output: false

**Constraints:**
- Case insensitive comparison
- Ignore non-alphanumeric characters
- Use recursion to solve this problem`,
    difficulty: 'Easy',
    tags: ['string', 'recursion', 'two-pointers'],
    hints: [
      'Clean the string first by removing non-alphanumeric characters and converting to lowercase',
      'Compare characters from both ends moving inward',
      'What is the base case for a palindrome check?'
    ],
    starterCode: `function isPalindrome(str: string): boolean {
  // Your code here
}`,
    solution: `function isPalindrome(str: string): boolean {
  const cleaned = str.toLowerCase().replace(/[^a-z0-9]/g, '');
  function checkPalindrome(s: string, left: number, right: number): boolean {
    if (left >= right) {
      return true;
    }
    if (s[left] !== s[right]) {
      return false;
    }
    return checkPalindrome(s, left + 1, right - 1);
  }
  return checkPalindrome(cleaned, 0, cleaned.length - 1);
}`,
    testCases: [
      {
        input: ["A man, a plan, a canal: Panama"],
        output: true
      },
      {
        input: ["race a car"],
        output: false
      },
      {
        input: ["Was it a car or a cat I saw?"],
        output: true
      },
      {
        input: ["hello"],
        output: false
      }
    ]
  },
  {
    id: 'string-3',
    title: 'Count Substrings',
    description: `Write a recursive function to count the number of occurrences of a substring in a string.

**Example 1:**
Input: str = "abababa", sub = "aba"
Output: 3
Explanation: "aba" appears at positions 0, 2, and 4

**Example 2:**
Input: str = "mississippi", sub = "issi"
Output: 2

**Constraints:**
- Overlapping substrings should be counted
- Case sensitive comparison
- Use recursion to solve this problem`,
    difficulty: 'Medium',
    tags: ['string', 'recursion', 'substring'],
    hints: [
      'Check if the substring exists at the current position',
      'Move one position ahead and count remaining occurrences',
      'Think about when to stop the recursion'
    ],
    starterCode: `function countSubstrings(str: string, sub: string): number {
  // Your code here
}`,
    solution: `function countSubstrings(str: string, sub: string): number {
  if (str.length < sub.length) {
    return 0;
  }
  if (str.startsWith(sub)) {
    return 1 + countSubstrings(str.slice(1), sub);
  }
  return countSubstrings(str.slice(1), sub);
}`,
    testCases: [
      {
        input: ["abababa", "aba"],
        output: 3
      },
      {
        input: ["mississippi", "issi"],
        output: 2
      },
      {
        input: ["aaa", "aa"],
        output: 2
      },
      {
        input: ["hello", "world"],
        output: 0
      }
    ]
  },
  {
    id: 'string-4',
    title: 'Generate Parentheses',
    description: `Write a recursive function to generate all combinations of well-formed parentheses.

**Example 1:**
Input: n = 3
Output: ["((()))","(()())","(())()","()(())","()()()"]

**Example 2:**
Input: n = 2
Output: ["(())","()()"]

**Constraints:**
- n will be positive
- Return all valid combinations
- Use recursion to solve this problem`,
    difficulty: 'Medium',
    tags: ['string', 'recursion', 'backtracking'],
    hints: [
      'Keep track of open and closed parentheses count',
      'You can add an open parenthesis if you haven\'t used all n',
      'You can add a closing parenthesis if you have more open than closed'
    ],
    starterCode: `function generateParentheses(n: number): string[] {
  // Your code here
}`,
    solution: `function generateParentheses(n: number): string[] {
  function backtrack(open: number, close: number, current: string, result: string[]) {
    if (current.length === 2 * n) {
      result.push(current);
      return;
    }
    if (open < n) {
      backtrack(open + 1, close, current + '(', result);
    }
    if (close < open) {
      backtrack(open, close + 1, current + ')', result);
    }
  }
  const result: string[] = [];
  backtrack(0, 0, '', result);
  return result;
}`,
    testCases: [
      {
        input: [3],
        output: ["((()))","(()())","(())()","()(())","()()()"]
      },
      {
        input: [2],
        output: ["(())","()()"]
      },
      {
        input: [1],
        output: ["()"]
      }
    ]
  },
  {
    id: 'string-5',
    title: 'Word Break',
    description: `Write a recursive function to determine if a string can be segmented into space-separated words from a dictionary.

**Example 1:**
Input: str = "leetcode", dict = ["leet","code"]
Output: true
Explanation: Return true because "leetcode" can be segmented as "leet code"

**Example 2:**
Input: str = "applepenapple", dict = ["apple","pen"]
Output: true

**Constraints:**
- Dictionary words are unique
- Case sensitive comparison
- Use recursion to solve this problem`,
    difficulty: 'Medium',
    tags: ['string', 'recursion', 'dynamic-programming'],
    hints: [
      'Try breaking the string at different positions',
      'Check if the first part is in dictionary and rest can be broken',
      'Think about using memoization to optimize'
    ],
    starterCode: `function wordBreak(str: string, dict: string[]): boolean {
  // Your code here
}`,
    solution: `function wordBreak(str: string, dict: string[]): boolean {
  function breakWord(s: string, wordDict: Set<string>, memo: { [key: string]: boolean }): boolean {
    if (s in memo) {
      return memo[s];
    }
    if (s === '') {
      return true;
    }
    for (let i = 1; i <= s.length; i++) {
      if (wordDict.has(s.slice(0, i)) && breakWord(s.slice(i), wordDict, memo)) {
        memo[s] = true;
        return true;
      }
    }
    memo[s] = false;
    return false;
  }
  const wordSet = new Set(dict);
  return breakWord(str, wordSet, {});
}`,
    testCases: [
      {
        input: ["leetcode", ["leet","code"]],
        output: true
      },
      {
        input: ["applepenapple", ["apple","pen"]],
        output: true
      },
      {
        input: ["catsandog", ["cats","dog","sand","and","cat"]],
        output: false
      }
    ]
  },
  {
    id: 'string-6',
    title: 'Decode String',
    description: `Write a recursive function to decode a string encoded with run-length encoding.

**Example 1:**
Input: str = "3[a]2[bc]"
Output: "aaabcbc"

**Example 2:**
Input: str = "2[abc]3[cd]ef"
Output: "abcabccdcdcdef"

**Constraints:**
- String will be well-formed
- Numbers will be positive integers
- Use recursion to solve this problem`,
    difficulty: 'Hard',
    tags: ['string', 'recursion', 'stack'],
    hints: [
      'Process the string character by character',
      'When you encounter a number, find its corresponding closing bracket',
      'Recursively decode the substring inside brackets'
    ],
    starterCode: `function decodeString(str: string): string {
  // Your code here
}`,
    solution: `function decodeString(str: string): string {
  function decode(s: string): string {
    let res = '';
    let multi = 0;
    let stack: string[] = [];
    for (let i = 0; i < s.length; i++) {
      if (!isNaN(Number(s[i]))) {
        multi = multi * 10 + Number(s[i]);
      } else if (s[i] === '[') {
        stack.push(res);
        stack.push(multi);
        res = '';
        multi = 0;
      } else if (s[i] === ']') {
        const curMulti = stack.pop() as number;
        const lastRes = stack.pop() as string;
        res = lastRes + curMulti * res;
      } else {
        res += s[i];
      }
    }
    return res;
  }
  return decode(str);
}`,
    testCases: [
      {
        input: ["3[a]2[bc]"],
        output: "aaabcbc"
      },
      {
        input: ["2[abc]3[cd]ef"],
        output: "abcabccdcdcdef"
      },
      {
        input: ["abc3[cd]xyz"],
        output: "abccdcdcdxyz"
      }
    ]
  },
  {
    id: 'string-7',
    title: 'Regular Expression Matching',
    description: `Write a recursive function to implement regular expression matching with support for '.' and '*'.
'.' Matches any single character.
'*' Matches zero or more of the preceding element.

**Example 1:**
Input: str = "aa", pattern = "a*"
Output: true
Explanation: '*' means zero or more of the preceding element, 'a'

**Example 2:**
Input: str = "ab", pattern = ".*"
Output: true
Explanation: ".*" means "zero or more (*) of any character (.)"`,
    difficulty: 'Hard',
    tags: ['string', 'recursion', 'dynamic-programming'],
    hints: [
      'Handle the case without * first',
      'For *, consider both using it and not using it',
      'Think about the base cases carefully'
    ],
    starterCode: `function isMatch(str: string, pattern: string): boolean {
  // Your code here
}`,
    solution: `function isMatch(str: string, pattern: string): boolean {
  function match(s: string, p: string): boolean {
    if (p === '') {
      return s === '';
    }
    const firstMatch = s !== '' && (p[0] === s[0] || p[0] === '.');
    if (p.length >= 2 && p[1] === '*') {
      return match(s, p.slice(2)) || (firstMatch && match(s.slice(1), p));
    } else {
      return firstMatch && match(s.slice(1), p.slice(1));
    }
  }
  return match(str, pattern);
}`,
    testCases: [
      {
        input: ["aa", "a*"],
        output: true
      },
      {
        input: ["ab", ".*"],
        output: true
      },
      {
        input: ["mississippi", "mis*is*p*."],
        output: false
      }
    ]
  },
  {
    id: 'string-8',
    title: 'Longest Common Subsequence',
    description: `Write a recursive function to find the length of the longest common subsequence between two strings.

**Example 1:**
Input: text1 = "abcde", text2 = "ace"
Output: 3
Explanation: The longest common subsequence is "ace"

**Example 2:**
Input: text1 = "abc", text2 = "abc"
Output: 3`,
    difficulty: 'Hard',
    tags: ['string', 'recursion', 'dynamic-programming'],
    hints: [
      'Compare characters from both strings',
      'If characters match, include it and move both pointers',
      'If they don\'t match, try skipping one character from either string'
    ],
    starterCode: `function longestCommonSubsequence(text1: string, text2: string): number {
  // Your code here
}`,
    solution: `function longestCommonSubsequence(text1: string, text2: string): number {
  function lcs(s1: string, s2: string): number {
    if (s1 === '' || s2 === '') {
      return 0;
    }
    if (s1[0] === s2[0]) {
      return 1 + lcs(s1.slice(1), s2.slice(1));
    }
    return Math.max(lcs(s1, s2.slice(1)), lcs(s1.slice(1), s2));
  }
  return lcs(text1, text2);
}`,
    testCases: [
      {
        input: ["abcde", "ace"],
        output: 3
      },
      {
        input: ["abc", "abc"],
        output: 3
      },
      {
        input: ["abc", "def"],
        output: 0
      }
    ]
  },
  {
    id: 'string-9',
    title: 'String Permutations',
    description: `Write a recursive function to generate all permutations of a string.
Each character in the string is unique.

**Example 1:**
Input: str = "abc"
Output: ["abc","acb","bac","bca","cab","cba"]

**Example 2:**
Input: str = "ab"
Output: ["ab","ba"]`,
    difficulty: 'Medium',
    tags: ['string', 'recursion', 'backtracking'],
    hints: [
      'Fix one character and find permutations of the rest',
      'Swap characters to generate different permutations',
      'Think about the base case of a single character'
    ],
    starterCode: `function generatePermutations(str: string): string[] {
  // Your code here
}`,
    solution: `function generatePermutations(str: string): string[] {
  if (str.length === 1) {
    return [str];
  }
  const result: string[] = [];
  for (let i = 0; i < str.length; i++) {
    const rest = str.slice(0, i) + str.slice(i + 1);
    for (const p of generatePermutations(rest)) {
      result.push(str[i] + p);
    }
  }
  return result;
}`,
    testCases: [
      {
        input: ["abc"],
        output: ["abc","acb","bac","bca","cab","cba"]
      },
      {
        input: ["ab"],
        output: ["ab","ba"]
      },
      {
        input: ["a"],
        output: ["a"]
      }
    ]
  },
  {
    id: 'string-10',
    title: 'Recursive String Compression',
    description: `Write a recursive function to perform basic string compression using counts of repeated characters.
If the compressed string is not smaller than the original string, return the original string.

**Example 1:**
Input: str = "aabbbcccc"
Output: "a2b3c4"

**Example 2:**
Input: str = "ab"
Output: "ab"
Explanation: "ab" is not compressed since length of compressed string would be longer`,
    difficulty: 'Medium',
    tags: ['string', 'recursion'],
    hints: [
      'Count consecutive characters',
      'Build the compressed string recursively',
      'Compare lengths before returning'
    ],
    starterCode: `function compressString(str: string): string {
  // Your code here
}`,
    solution: `function compressString(str: string): string {
  function compress(s: string): string {
    if (s.length <= 1) {
      return s;
    }
    let count = 1;
    let res = '';
    for (let i = 1; i <= s.length; i++) {
      if (i === s.length || s[i] !== s[i - 1]) {
        res += s[i - 1] + (count > 1 ? count.toString() : '');
        count = 1;
      } else {
        count++;
      }
    }
    return res;
  }
  const compressed = compress(str);
  return compressed.length < str.length ? compressed : str;
}`,
    testCases: [
      {
        input: ["aabbbcccc"],
        output: "a2b3c4"
      },
      {
        input: ["ab"],
        output: "ab"
      },
      {
        input: ["aaa"],
        output: "a3"
      },
      {
        input: ["aaabbaaa"],
        output: "a3b2a3"
      }
    ]
  }
];